/**
 * 体験カレンダー — Taiken Experience Calendar.
 *
 * Content comes from the `taiken` custom post type, injected by PHP as a JSON
 * island (#taiken-hub-data). Japanese is the default locale; English is fully
 * translated and switchable but hidden unless enabled.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ThemeProvider, alpha, useTheme } from '@mui/material/styles';

import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import UmbrellaIcon from '@mui/icons-material/Umbrella';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ViewDayIcon from '@mui/icons-material/ViewDay';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SellIcon from '@mui/icons-material/Sell';
import GroupsIcon from '@mui/icons-material/Groups';
import PlaceIcon from '@mui/icons-material/Place';

import { BRAND, createTaikenTheme } from './taiken/theme.js';
import { createTranslator, localized, DEFAULT_LOCALE } from './taiken/i18n.js';
import {
    closureFor,
    formatDateLabel,
    formatShortDate,
    getMonthMeta,
    pad2,
    parseISODate,
    toISODate,
} from './taiken/dates.js';
import {
    assignRunLanes,
    buildTimeline,
    capacityForDate,
    collectCategories,
    dayTypeKey,
    describeByDayType,
    eventRunsOnDate,
    filterEvents,
    isMultiDay,
    occurrenceDates,
    slotsForDate,
} from './taiken/schedule.js';

/* -------------------------------------------------------------------------
 * Bootstrap data
 * ---------------------------------------------------------------------- */

const EMPTY_PAYLOAD = {
    events: [],
    holidays: {},
    today: toISODate(new Date()),
    locale: DEFAULT_LOCALE,
    openHours: { start: 10, end: 17 },
    closedDays: {},
    closedWeekdays: [2, 3],
    showLocaleToggle: false,
};

/**
 * Read the JSON island printed by PHP.
 *
 * @returns {Object} Payload, or empty defaults if absent or malformed.
 */
function readPayload() {
    const node = document.getElementById('taiken-hub-data');
    if (!node) {
        return EMPTY_PAYLOAD;
    }
    try {
        return { ...EMPTY_PAYLOAD, ...JSON.parse(node.textContent) };
    } catch (error) {
        // A malformed payload should degrade to an empty calendar, not a blank page.
        if (window.console && window.console.error) {
            window.console.error('[taiken] could not parse calendar data', error);
        }
        return EMPTY_PAYLOAD;
    }
}

/**
 * Locale requested via ?lang=, for previewing English before it goes live.
 *
 * @returns {string|null}
 */
function localeFromQuery() {
    try {
        const value = new URLSearchParams(window.location.search).get('lang');
        return value === 'en' || value === 'ja' ? value : null;
    } catch (error) {
        return null;
    }
}

/* -------------------------------------------------------------------------
 * Presentational pieces
 * ---------------------------------------------------------------------- */

function CategoryChips({ event, locale, t }) {
    return (
        <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
            {event.categories.map((cat) => (
                <Chip
                    key={`${event.id}-${cat.slug}`}
                    size="small"
                    label={localized(locale, cat.label, cat.labelEn)}
                    sx={{ bgcolor: alpha(cat.color, 0.12), color: cat.color }}
                />
            ))}
            <Chip
                size="small"
                variant="outlined"
                label={t(`weather_${event.weather}`)}
                sx={{ borderColor: alpha(BRAND.green, 0.35), color: BRAND.ink }}
            />
        </Stack>
    );
}

function EventImage({ event, height, rounded }) {
    if (!event.image) {
        return (
            <Box sx={{ height, bgcolor: alpha(BRAND.moss, 0.12), borderRadius: rounded ? 1.2 : 0 }} />
        );
    }

    return (
        <Box
            component="img"
            src={event.image}
            srcSet={event.imageSrcset || undefined}
            sizes="(max-width: 900px) 100vw, 800px"
            alt=""
            loading="lazy"
            decoding="async"
            sx={{
                display: 'block',
                width: '100%',
                height,
                objectFit: 'cover',
                borderRadius: rounded ? 1.2 : 0,
            }}
        />
    );
}

function EventCard({ event, slotLabel, capacity, locale, t, onOpenDetails }) {
    const notes = localized(locale, event.notes, event.notesEn);
    const facts = [
        { icon: <AccessTimeIcon fontSize="small" />, value: slotLabel },
        { icon: <SellIcon fontSize="small" />, value: localized(locale, event.price, event.priceEn) },
        { icon: <GroupsIcon fontSize="small" />, value: capacity !== undefined ? capacity : localized(locale, event.capacity, event.capacityEn) },
        { icon: <PlaceIcon fontSize="small" />, value: localized(locale, event.booking, event.bookingEn) },
    ].filter((fact) => fact.value);

    return (
        <Card variant="outlined">
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
                <Box sx={{ flex: { sm: '0 0 34%' }, minWidth: 0 }}>
                    <EventImage event={event} height={160} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <CardContent>
                        <CategoryChips event={event} locale={locale} t={t} />

                        <Typography variant="h6" sx={{ mt: 1.1, fontSize: '1.08rem', fontWeight: 700 }}>
                            {localized(locale, event.title, event.titleEn)}
                        </Typography>

                        {localized(locale, event.summary, event.summaryEn) && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {localized(locale, event.summary, event.summaryEn)}
                            </Typography>
                        )}

                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={{ xs: 0.5, md: 1.6 }}
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ mt: 1.2 }}
                        >
                            {facts.map((fact) => (
                                <Stack key={fact.value} direction="row" spacing={0.6} alignItems="center">
                                    {fact.icon}
                                    <Typography variant="body2">{fact.value}</Typography>
                                </Stack>
                            ))}
                        </Stack>

                        {notes && (
                            <Typography
                                variant="caption"
                                sx={{ display: 'block', mt: 1, color: BRAND.clay, lineHeight: 1.6 }}
                            >
                                {notes}
                            </Typography>
                        )}

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => onOpenDetails(event)}
                            sx={{ mt: 1.6, borderRadius: 8 }}
                        >
                            {t('details')}
                        </Button>
                    </CardContent>
                </Box>
            </Box>
        </Card>
    );
}

/**
 * Compact markers for events that began in an earlier hour and are still running.
 */
function OngoingRow({ entries, locale, t, onOpenDetails }) {
    return (
        <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
            {entries.map(({ event, slot }) => (
                <Chip
                    key={`${event.id}-${slot.label}-ongoing`}
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => onOpenDetails(event)}
                    label={`${localized(locale, event.title, event.titleEn)}　${t('ongoing')}・${t('untilTime', { time: slot.endLabel })}`}
                    sx={{
                        borderStyle: 'dashed',
                        borderColor: alpha(BRAND.green, 0.4),
                        color: alpha(BRAND.ink, 0.75),
                    }}
                />
            ))}
        </Stack>
    );
}

function TimelineView({ date, events, openHours, locale, t, holidays, closure, onOpenDetails }) {
    const timeline = useMemo(
        () => buildTimeline(events, openHours, date, holidays),
        [events, openHours, date, holidays]
    );
    const holidayName = holidays[toISODate(date)];
    const isClosed = Boolean(closure && closure.closed);
    // A closed day can still carry an entry whose dates were typed in by hand.
    const hasEvents = events.length > 0;

    return (
        <Box>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 1.5 }}
            >
                <Typography variant="h6">
                    {t('timelineFor', { date: formatDateLabel(date, locale) })}
                </Typography>
                {holidayName && !isClosed && (
                    <Chip
                        size="small"
                        label={locale === 'en' ? t('holiday') : holidayName}
                        sx={{ bgcolor: alpha(BRAND.clay, 0.16), color: BRAND.clay }}
                    />
                )}
                {isClosed && (
                    <Chip
                        size="small"
                        label={locale === 'en' ? t('closed') : closure.reason || t('closed')}
                        sx={{ bgcolor: alpha(BRAND.ink, 0.1), color: alpha(BRAND.ink, 0.75) }}
                    />
                )}
            </Stack>

            {isClosed && !hasEvents && (
                <Paper variant="outlined" sx={{ p: 2.5, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {t('closedToday')}
                    </Typography>
                </Paper>
            )}

            {!hasEvents && !isClosed && timeline.isEmpty && (
                <Paper variant="outlined" sx={{ p: 2.5, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {t('noEventsToday')}
                    </Typography>
                </Paper>
            )}

            {timeline.allDay.length > 0 && (
                <Stack spacing={1.25} sx={{ mb: 2 }}>
                    {timeline.allDay.map((event) => (
                        <EventCard
                            key={`${event.id}-allday`}
                            event={event}
                            slotLabel={localized(locale, event.timeNote, event.timeNoteEn) || t('days_specific')}
                            capacity={capacityForDate(event, date, holidays)}
                            locale={locale}
                            t={t}
                            onOpenDetails={onOpenDetails}
                        />
                    ))}
                </Stack>
            )}

            <Stack spacing={1.5}>
                {hasEvents && timeline.hours.map((row) => (
                    <Paper key={row.hour} variant="outlined" sx={{ p: 1.5 }}>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '72px 1fr' },
                                gap: 1.5,
                                alignItems: 'start',
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: BRAND.green }}>
                                {pad2(row.hour)}:00
                            </Typography>

                            <Box sx={{ minWidth: 0 }}>
                                {row.starting.length === 0 && row.ongoing.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {t('noEventsInHour')}
                                    </Typography>
                                ) : (
                                    <Stack spacing={1.25}>
                                        {row.starting.map(({ event, slot }) => (
                                            <EventCard
                                                key={`${event.id}-${slot.label}`}
                                                event={event}
                                                slotLabel={slot.label}
                                                capacity={capacityForDate(event, date, holidays)}
                                                locale={locale}
                                                t={t}
                                                onOpenDetails={onOpenDetails}
                                            />
                                        ))}
                                        {row.ongoing.length > 0 && (
                                            <OngoingRow
                                                entries={row.ongoing}
                                                locale={locale}
                                                t={t}
                                                onOpenDetails={onOpenDetails}
                                            />
                                        )}
                                    </Stack>
                                )}
                            </Box>
                        </Box>
                    </Paper>
                ))}
            </Stack>
        </Box>
    );
}

function EventDetailsDialog({ event, open, locale, t, onClose }) {
    if (!event) {
        return null;
    }

    const detailHtml = locale === 'en' ? '' : event.detail;
    const detailText = locale === 'en' ? event.detailEn || '' : '';
    const timeNote = localized(locale, event.timeNote, event.timeNoteEn);
    const notes = localized(locale, event.notes, event.notesEn);
    // The dialog is not tied to a date, so a value that changes between
    // weekdays and weekends has to show both rather than pick one.
    const joinSlots = (slots) => (slots || []).join('　/　');
    const timeValue =
        timeNote || describeByDayType(event.timeSlotsWeekday, event.timeSlots, t, joinSlots);
    const capacityValue = describeByDayType(
        localized(locale, event.capacityWeekday, event.capacityWeekdayEn),
        localized(locale, event.capacity, event.capacityEn),
        t
    );

    // What other dates does this run on? A continuous range is shown as a span;
    // scattered dates are listed. A purely recurring event lists nothing —
    // the "days" row already says weekends and holidays.
    const dates = occurrenceDates(event);
    const isRange = Boolean(event.rangeStart && event.rangeEnd && event.rangeEnd > event.rangeStart);
    const MAX_DATES = 8;
    let datesValue = '';
    if (isRange) {
        datesValue = t('runsUntil', {
            start: formatShortDate(event.rangeStart, locale),
            end: formatShortDate(event.rangeEnd, locale),
        });
    } else if (dates && dates.length > 0) {
        const shown = dates.slice(0, MAX_DATES).map((iso) => formatShortDate(iso, locale));
        datesValue =
            dates.length > MAX_DATES
                ? `${shown.join('・')}　${t('moreDates', { count: dates.length - MAX_DATES })}`
                : shown.join('・');
    }

    /*
     * One row for when it runs, not two. Real dates beat a recurrence rule:
     * "9月5日(土)・9月6日(日)" tells a visitor more than "weekends and
     * holidays", and a ranged event has no meaningful day-type at all.
     */
    const whenRow = isRange
        ? { label: t('labelRange'), value: datesValue }
        : datesValue
          ? { label: t('otherDates'), value: datesValue }
          : { label: t('labelDays'), value: t(dayTypeKey(event)) };

    const rows = [
        whenRow,
        { label: t('labelTime'), value: timeValue },
        { label: t('labelPrice'), value: localized(locale, event.price, event.priceEn) },
        { label: t('labelCapacity'), value: capacityValue },
        { label: t('labelBooking'), value: localized(locale, event.booking, event.bookingEn) },
    ].filter((row) => row.value);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
            <DialogTitle sx={{ pb: 1, pr: 6, fontWeight: 800 }}>
                {localized(locale, event.title, event.titleEn)}
                <IconButton
                    aria-label={t('close')}
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={1.4}>
                    <EventImage event={event} height={220} rounded />
                    <CategoryChips event={event} locale={locale} t={t} />

                    {rows.map((row) => (
                        <Typography key={row.label} variant="body2">
                            <strong>{row.label}:</strong> {row.value}
                        </Typography>
                    ))}

                    {notes && (
                        <Typography variant="body2" sx={{ color: BRAND.clay, lineHeight: 1.7 }}>
                            {notes}
                        </Typography>
                    )}

                    {localized(locale, event.summary, event.summaryEn) && (
                        <>
                            <Divider sx={{ borderColor: alpha(BRAND.green, 0.15) }} />
                            <Typography variant="body2" color="text.secondary">
                                {localized(locale, event.summary, event.summaryEn)}
                            </Typography>
                        </>
                    )}

                    {detailHtml && (
                        <Box
                            sx={{
                                color: 'text.secondary',
                                fontSize: '0.875rem',
                                lineHeight: 1.8,
                                '& p': { margin: '0 0 0.8em' },
                            }}
                            /* Editor content, already sanitised with wp_kses_post() server-side. */
                            dangerouslySetInnerHTML={{ __html: detailHtml }}
                        />
                    )}

                    {detailText && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}
                        >
                            {detailText}
                        </Typography>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                {event.permalink && (
                    <Button href={event.permalink} color="primary">
                        {t('details')}
                    </Button>
                )}
                <Button onClick={onClose} color="primary">
                    {t('close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function MonthGrid({ monthValue, events, holidays, closedDays, closedWeekdays, locale, t, onSelectDay }) {
    const meta = useMemo(() => getMonthMeta(monthValue), [monthValue]);

    const { cells, laneCount } = useMemo(() => {
        if (!meta) {
            return { cells: [], laneCount: 0 };
        }

        const firstIso = toISODate(new Date(meta.year, meta.month - 1, 1));
        const lastIso = toISODate(new Date(meta.year, meta.month - 1, meta.daysInMonth));
        const segments = assignRunLanes(events, firstIso, lastIso);
        const lanes = segments.length > 0
            ? Math.min(Math.max(...segments.map((segment) => segment.lane)) + 1, 3)
            : 0;

        const list = [];
        for (let i = 0; i < meta.firstWeekday; i += 1) {
            list.push(null);
        }
        for (let day = 1; day <= meta.daysInMonth; day += 1) {
            const date = new Date(meta.year, meta.month - 1, day);
            const iso = toISODate(date);
            const closure = closureFor(date, closedDays, closedWeekdays, holidays);
            const running = events.filter((event) => eventRunsOnDate(event, date, holidays, closure));

            // One slot per lane so a band lines up across the days it covers.
            const bands = [];
            for (let lane = 0; lane < lanes; lane += 1) {
                const segment = segments.find(
                    (item) => item.lane === lane && iso >= item.start && iso <= item.end
                );
                bands.push(
                    segment
                        ? {
                              segment,
                              isRunStart: iso === segment.trueStart,
                              isRunEnd: iso === segment.trueEnd,
                              // Repeat the name at the start of each week.
                              showLabel: iso === segment.trueStart || date.getDay() === 0,
                              // Drawn through a closed day, but muted.
                              muted: closure.closed,
                          }
                        : null
                );
            }

            list.push({
                day,
                iso,
                weekday: date.getDay(),
                holiday: holidays[iso] || '',
                closure,
                bands,
                events: running,
                // Multi-day entries are shown as a band, so keep them out of the dots.
                dotEvents: running.filter((event) => !isMultiDay(event)),
            });
        }
        return { cells: list, laneCount: lanes };
    }, [meta, events, holidays, closedDays, closedWeekdays]);

    if (!meta) {
        return null;
    }

    return (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
                {t('weekdays').map((label, index) => (
                    <Box
                        key={label}
                        sx={{
                            p: 1,
                            bgcolor: alpha(BRAND.green, 0.08),
                            borderBottom: `1px solid ${alpha(BRAND.green, 0.18)}`,
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            align="center"
                            sx={{
                                fontWeight: 800,
                                color: index === 0 ? BRAND.clay : index === 6 ? '#5B7E9A' : BRAND.green,
                            }}
                        >
                            {label}
                        </Typography>
                    </Box>
                ))}

                {cells.map((cell, index) => {
                    if (!cell) {
                        return (
                            <Box
                                // eslint-disable-next-line react/no-array-index-key
                                key={`empty-${index}`}
                                sx={{
                                    minHeight: { xs: 84 + laneCount * 21, md: 108 + laneCount * 21 },
                                    borderRight: `1px solid ${alpha(BRAND.green, 0.12)}`,
                                    borderBottom: `1px solid ${alpha(BRAND.green, 0.12)}`,
                                    bgcolor: alpha(BRAND.sand, 0.35),
                                }}
                            />
                        );
                    }

                    const isRest = cell.weekday === 0 || Boolean(cell.holiday);
                    const isClosed = cell.closure.closed;
                    const dotColors = Array.from(
                        new Map(
                            cell.dotEvents
                                .flatMap((event) => event.categories)
                                .map((cat) => [cat.slug, cat.color])
                        ).values()
                    ).slice(0, 4);

                    return (
                        <Box
                            key={cell.iso}
                            role="button"
                            tabIndex={0}
                            aria-label={t('openDay', { date: cell.iso })}
                            onClick={() => onSelectDay(cell.iso)}
                            onKeyDown={(bubble) => {
                                if (bubble.key === 'Enter' || bubble.key === ' ') {
                                    bubble.preventDefault();
                                    onSelectDay(cell.iso);
                                }
                            }}
                            sx={{
                                minHeight: { xs: 84 + laneCount * 21, md: 108 + laneCount * 21 },
                                p: 0.9,
                                borderRight: `1px solid ${alpha(BRAND.green, 0.12)}`,
                                borderBottom: `1px solid ${alpha(BRAND.green, 0.12)}`,
                                bgcolor: isClosed ? alpha(BRAND.ink, 0.05) : '#fff',
                                cursor: 'pointer',
                                textAlign: 'left',
                                '&:hover': { bgcolor: alpha(BRAND.moss, 0.07) },
                                '&:focus-visible': { outline: `2px solid ${BRAND.green}`, outlineOffset: -2 },
                            }}
                        >
                            <Stack spacing={0.6}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 700,
                                        color: isRest ? BRAND.clay : cell.weekday === 6 ? '#5B7E9A' : BRAND.ink,
                                    }}
                                >
                                    {cell.day}
                                </Typography>

                                {cell.holiday && !isClosed && locale === 'ja' && (
                                    <Typography variant="caption" sx={{ color: BRAND.clay, lineHeight: 1.2 }}>
                                        {cell.holiday}
                                    </Typography>
                                )}

                                {isClosed && (
                                    <Typography
                                        variant="caption"
                                        sx={{ color: alpha(BRAND.ink, 0.55), lineHeight: 1.2 }}
                                    >
                                        {locale === 'en' ? t('closed') : '休園日'}
                                    </Typography>
                                )}

                                {laneCount > 0 && (
                                    <Box sx={{ mx: -0.9 }}>
                                        {cell.bands.map((band, lane) => {
                                            if (!band) {
                                                // Empty slot keeps later lanes aligned.
                                                return (
                                                    <Box
                                                        // eslint-disable-next-line react/no-array-index-key
                                                        key={`${cell.iso}-lane-${lane}`}
                                                        sx={{ height: 18, mb: 0.3 }}
                                                    />
                                                );
                                            }
                                            const { segment, isRunStart, isRunEnd, showLabel, muted } = band;
                                            const colour =
                                                (segment.event.categories[0] &&
                                                    segment.event.categories[0].color) ||
                                                BRAND.green;
                                            return (
                                                <Box
                                                    key={`${cell.iso}-lane-${lane}`}
                                                    title={localized(
                                                        locale,
                                                        segment.event.title,
                                                        segment.event.titleEn
                                                    )}
                                                    sx={{
                                                        height: 18,
                                                        mb: 0.3,
                                                        px: 0.6,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        overflow: 'hidden',
                                                        bgcolor: muted ? alpha(colour, 0.28) : alpha(colour, 0.9),
                                                        color: muted ? alpha(BRAND.ink, 0.55) : '#fff',
                                                        // Round only the true ends, so the
                                                        // middle days read as one band.
                                                        borderTopLeftRadius: isRunStart ? 9 : 0,
                                                        borderBottomLeftRadius: isRunStart ? 9 : 0,
                                                        borderTopRightRadius: isRunEnd ? 9 : 0,
                                                        borderBottomRightRadius: isRunEnd ? 9 : 0,
                                                        marginLeft: isRunStart ? '3px' : 0,
                                                        marginRight: isRunEnd ? '3px' : 0,
                                                    }}
                                                >
                                                    {showLabel && (
                                                        <Typography
                                                            variant="caption"
                                                            noWrap
                                                            sx={{
                                                                fontSize: 10,
                                                                fontWeight: 700,
                                                                lineHeight: 1,
                                                                // Crop rather than widen the column.
                                                                minWidth: 0,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                            }}
                                                        >
                                                            {localized(
                                                                locale,
                                                                segment.event.title,
                                                                segment.event.titleEn
                                                            )}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}

                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {dotColors.map((color) => (
                                        <Box
                                            key={`${cell.iso}-${color}`}
                                            sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: color }}
                                        />
                                    ))}
                                </Stack>

                                {cell.events.length > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                        {t('eventCount', { count: cell.events.length })}
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
}

function EventTable({ events, locale, t, onOpenDetails }) {
    return (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small">
                <TableHead>
                    <TableRow sx={{ bgcolor: alpha(BRAND.green, 0.08) }}>
                        {['colEvent', 'colDays', 'colWeather', 'colTime', 'colBooking'].map((key) => (
                            <TableCell
                                key={key}
                                sx={{ fontWeight: 800, color: BRAND.green, whiteSpace: 'nowrap' }}
                            >
                                {t(key)}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {events.map((event) => (
                        <TableRow
                            key={event.id}
                            hover
                            tabIndex={0}
                            onClick={() => onOpenDetails(event)}
                            onKeyDown={(bubble) => {
                                if (bubble.key === 'Enter') {
                                    onOpenDetails(event);
                                }
                            }}
                            sx={{ cursor: 'pointer' }}
                        >
                            <TableCell sx={{ minWidth: 260 }}>
                                <Stack direction="row" spacing={1.1} alignItems="center">
                                    <Box sx={{ width: 88, flexShrink: 0 }}>
                                        <EventImage event={event} height={58} rounded />
                                    </Box>
                                    <Stack sx={{ minWidth: 0 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            {localized(locale, event.title, event.titleEn)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {event.categories
                                                .map((cat) => localized(locale, cat.label, cat.labelEn))
                                                .join(' / ')}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </TableCell>

                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{t(dayTypeKey(event))}</TableCell>

                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Chip
                                    size="small"
                                    label={t(`weather_${event.weather}`)}
                                    sx={{ bgcolor: alpha(BRAND.moss, 0.14), color: BRAND.ink }}
                                />
                            </TableCell>

                            <TableCell>
                                <Typography variant="body2">
                                    {localized(locale, event.timeNote, event.timeNoteEn) ||
                                        describeByDayType(event.timeSlotsWeekday, event.timeSlots, t, (slots) =>
                                            (slots || []).join(', ')
                                        )}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {describeByDayType(
                                        localized(locale, event.capacityWeekday, event.capacityWeekdayEn),
                                        localized(locale, event.capacity, event.capacityEn),
                                        t
                                    )}
                                </Typography>
                            </TableCell>

                            <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {localized(locale, event.price, event.priceEn)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {localized(locale, event.booking, event.bookingEn)}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Paper>
    );
}

/* -------------------------------------------------------------------------
 * App
 * ---------------------------------------------------------------------- */

function TaikenCalendar({ payload }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [locale, setLocale] = useState(localeFromQuery() || payload.locale || DEFAULT_LOCALE);
    const [primaryView, setPrimaryView] = useState('day');
    const [weatherPlan, setWeatherPlan] = useState('sunny');
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [targetFilter, setTargetFilter] = useState('all');
    // Seeded from the server clock, so "today" is Japan time for every visitor.
    const [selectedDateStr, setSelectedDateStr] = useState(payload.today);
    const [monthlyChildView, setMonthlyChildView] = useState('grid');
    const [drawerDateStr, setDrawerDateStr] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);

    const t = useMemo(() => createTranslator(locale), [locale]);
    const holidays = payload.holidays || {};
    const openHours = payload.openHours || { start: 10, end: 17 };
    const closedDays = payload.closedDays || {};
    const closedWeekdays = payload.closedWeekdays || [];

    const selectedDate = useMemo(() => parseISODate(selectedDateStr) || new Date(), [selectedDateStr]);
    const monthValue = selectedDateStr.slice(0, 7);

    const categories = useMemo(() => collectCategories(payload.events), [payload.events]);

    const visibleEvents = useMemo(
        () =>
            filterEvents(payload.events, {
                plan: weatherPlan,
                category: categoryFilter,
                target: targetFilter,
                search,
                locale,
            }),
        [payload.events, weatherPlan, categoryFilter, targetFilter, search, locale]
    );

    const selectedClosure = useMemo(
        () => closureFor(selectedDate, closedDays, closedWeekdays, holidays),
        [selectedDate, closedDays, closedWeekdays, holidays]
    );

    const dayEvents = useMemo(
        () =>
            visibleEvents.filter((event) =>
                eventRunsOnDate(event, selectedDate, holidays, selectedClosure)
            ),
        [visibleEvents, selectedDate, holidays, selectedClosure]
    );

    const drawerDate = useMemo(() => parseISODate(drawerDateStr), [drawerDateStr]);
    const drawerClosure = useMemo(
        () => (drawerDate ? closureFor(drawerDate, closedDays, closedWeekdays, holidays) : null),
        [drawerDate, closedDays, closedWeekdays, holidays]
    );
    const drawerEvents = useMemo(
        () =>
            drawerDate
                ? visibleEvents.filter((event) =>
                      eventRunsOnDate(event, drawerDate, holidays, drawerClosure)
                  )
                : [],
        [drawerDate, visibleEvents, holidays, drawerClosure]
    );

    const closeDrawer = useCallback(() => setDrawerDateStr(null), []);
    const closeDialog = useCallback(() => setSelectedEvent(null), []);

    if (payload.events.length === 0) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {t('noEventsToday')}
                    </Typography>
                </Paper>
            </Container>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: BRAND.sand,
                backgroundImage:
                    'radial-gradient(circle at 10% 5%, rgba(122,154,125,0.18) 0%, rgba(122,154,125,0.01) 35%), radial-gradient(circle at 95% 0%, rgba(185,130,90,0.14) 0%, rgba(185,130,90,0.01) 30%)',
                /*
                 * Top padding clears the theme's fixed header, which occupies
                 * roughly the first 53px on desktop and 39px on mobile; the
                 * rest is breathing room so the heading does not sit under it.
                 * Side padding clears the fixed rails (.kv-txtL 50px,
                 * .kvAcess ~53px), which are hidden below the PC breakpoint.
                 */
                pt: { xs: '80px', md: '125px' },
                pr: { xs: 0, md: '62px' },
                pb: { xs: '24px', md: '32px' },
                pl: { xs: 0, md: '62px' },
            }}
        >
            <Container maxWidth="lg">
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, md: 3 },
                        borderRadius: 3,
                        border: `1px solid ${alpha(BRAND.green, 0.18)}`,
                        bgcolor: alpha('#ffffff', 0.92),
                    }}
                >
                    <Stack spacing={2.25}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                            <Stack spacing={0.4}>
                                <Typography
                                    variant="overline"
                                    sx={{ color: BRAND.green, letterSpacing: 1.2, fontWeight: 700 }}
                                >
                                    {t('overline')}
                                </Typography>
                                <Typography
                                    component="h1"
                                    variant="h4"
                                    sx={{ fontWeight: 800, fontSize: { xs: '1.5rem', md: '2.1rem' }, lineHeight: 1.15 }}
                                >
                                    {t('heading')}
                                </Typography>
                            </Stack>

                            {payload.showLocaleToggle && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => setLocale(locale === 'ja' ? 'en' : 'ja')}
                                >
                                    {locale === 'ja' ? t('switchToEnglish') : t('switchToJapanese')}
                                </Button>
                            )}
                        </Stack>

                        <Divider sx={{ borderColor: alpha(BRAND.green, 0.16) }} />

                        <Stack spacing={1.6}>
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1.25}
                                alignItems={{ xs: 'stretch', md: 'center' }}
                                justifyContent="space-between"
                            >
                                <ToggleButtonGroup
                                    exclusive
                                    value={primaryView}
                                    onChange={(_, value) => value && setPrimaryView(value)}
                                    aria-label={t('heading')}
                                >
                                    <ToggleButton value="day">
                                        <ViewDayIcon sx={{ mr: 0.8 }} />
                                        {t('dayView')}
                                    </ToggleButton>
                                    <ToggleButton value="monthly">
                                        <CalendarMonthIcon sx={{ mr: 0.8 }} />
                                        {t('monthlyView')}
                                    </ToggleButton>
                                </ToggleButtonGroup>

                                <ToggleButtonGroup
                                    exclusive
                                    value={weatherPlan}
                                    onChange={(_, value) => value && setWeatherPlan(value)}
                                >
                                    <ToggleButton value="sunny">
                                        <WbSunnyIcon sx={{ mr: 0.7 }} />
                                        {t('sunnyPlan')}
                                    </ToggleButton>
                                    <ToggleButton value="rainy">
                                        <UmbrellaIcon sx={{ mr: 0.7 }} />
                                        {t('rainyPlan')}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>

                            <Paper variant="outlined" sx={{ p: 1.2, bgcolor: alpha('#fff', 0.86) }}>
                                <Stack spacing={1.2}>
                                    <TextField
                                        size="small"
                                        placeholder={t('searchPlaceholder')}
                                        value={search}
                                        onChange={(bubble) => setSearch(bubble.target.value)}
                                        fullWidth
                                        inputProps={{ 'aria-label': t('searchPlaceholder') }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon sx={{ color: alpha(BRAND.ink, 0.6) }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: search ? (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label={t('clearSearch')}
                                                        size="small"
                                                        onClick={() => setSearch('')}
                                                        edge="end"
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            ) : null,
                                        }}
                                    />

                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                        {[{ slug: 'all', label: t('categoryAll'), labelEn: t('categoryAll'), color: BRAND.green }]
                                            .concat(categories)
                                            .map((cat) => (
                                                <Chip
                                                    key={cat.slug}
                                                    label={localized(locale, cat.label, cat.labelEn)}
                                                    clickable
                                                    onClick={() => setCategoryFilter(cat.slug)}
                                                    aria-pressed={categoryFilter === cat.slug}
                                                    /*
                                                     * Carrying the category's own colour makes these
                                                     * double as the legend for the dots in the month
                                                     * grid, which otherwise have nothing to decode them.
                                                     */
                                                    sx={{
                                                        bgcolor:
                                                            cat.slug === 'all'
                                                                ? categoryFilter === 'all'
                                                                    ? alpha(BRAND.ink, 0.12)
                                                                    : 'transparent'
                                                                : alpha(cat.color, categoryFilter === cat.slug ? 1 : 0.16),
                                                        color:
                                                            cat.slug !== 'all' && categoryFilter === cat.slug
                                                                ? '#fff'
                                                                : BRAND.ink,
                                                        border: `1px solid ${alpha(cat.color, 0.45)}`,
                                                        fontWeight: categoryFilter === cat.slug ? 700 : 600,
                                                        '&:hover': {
                                                            bgcolor:
                                                                cat.slug === 'all'
                                                                    ? alpha(BRAND.ink, 0.08)
                                                                    : alpha(cat.color, categoryFilter === cat.slug ? 1 : 0.3),
                                                        },
                                                    }}
                                                />
                                            ))}
                                    </Stack>

                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                        {['all', 'family_kids', 'couples_adults'].map((key) => (
                                            <Chip
                                                key={key}
                                                label={key === 'all' ? t('targetAll') : t(`target_${key}`)}
                                                clickable
                                                onClick={() => setTargetFilter(key)}
                                                variant={targetFilter === key ? 'filled' : 'outlined'}
                                                aria-pressed={targetFilter === key}
                                                sx={{
                                                    bgcolor: targetFilter === key ? alpha(BRAND.clay, 0.2) : 'transparent',
                                                    borderColor: alpha(BRAND.clay, 0.35),
                                                    fontWeight: targetFilter === key ? 700 : 500,
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Stack>
                            </Paper>
                        </Stack>

                        {primaryView === 'day' && (
                            <Stack spacing={1.5}>
                                <TextField
                                    label={t('selectedDate')}
                                    type="date"
                                    size="small"
                                    value={selectedDateStr}
                                    onChange={(bubble) => bubble.target.value && setSelectedDateStr(bubble.target.value)}
                                    sx={{ width: isMobile ? '100%' : 240 }}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TimelineView
                                    date={selectedDate}
                                    events={dayEvents}
                                    openHours={openHours}
                                    locale={locale}
                                    t={t}
                                    holidays={holidays}
                                    closure={selectedClosure}
                                    onOpenDetails={setSelectedEvent}
                                />
                            </Stack>
                        )}

                        {primaryView === 'monthly' && (
                            <Stack spacing={1.5}>
                                <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    spacing={1}
                                    justifyContent="space-between"
                                    alignItems={{ xs: 'stretch', md: 'center' }}
                                >
                                    <TextField
                                        label={t('month')}
                                        type="month"
                                        size="small"
                                        value={monthValue}
                                        onChange={(bubble) => {
                                            if (bubble.target.value) {
                                                setSelectedDateStr(`${bubble.target.value}-01`);
                                            }
                                        }}
                                        sx={{ width: isMobile ? '100%' : 210 }}
                                        InputLabelProps={{ shrink: true }}
                                    />

                                    <ToggleButtonGroup
                                        exclusive
                                        value={monthlyChildView}
                                        onChange={(_, value) => value && setMonthlyChildView(value)}
                                    >
                                        <ToggleButton value="grid">
                                            <ViewModuleIcon sx={{ mr: 0.7 }} />
                                            {t('gridCalendar')}
                                        </ToggleButton>
                                        <ToggleButton value="list">
                                            <ViewListIcon sx={{ mr: 0.7 }} />
                                            {t('listView')}
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Stack>

                                {monthlyChildView === 'grid' ? (
                                    <MonthGrid
                                        monthValue={monthValue}
                                        events={visibleEvents}
                                        holidays={holidays}
                                        closedDays={closedDays}
                                        closedWeekdays={closedWeekdays}
                                        locale={locale}
                                        t={t}
                                        onSelectDay={setDrawerDateStr}
                                    />
                                ) : (
                                    <EventTable
                                        events={visibleEvents}
                                        locale={locale}
                                        t={t}
                                        onOpenDetails={setSelectedEvent}
                                    />
                                )}
                            </Stack>
                        )}
                    </Stack>
                </Paper>
            </Container>

            <EventDetailsDialog
                event={selectedEvent}
                open={Boolean(selectedEvent)}
                locale={locale}
                t={t}
                onClose={closeDialog}
            />

            <Drawer
                anchor={isMobile ? 'bottom' : 'right'}
                open={Boolean(drawerDateStr)}
                onClose={closeDrawer}
                PaperProps={{
                    sx: {
                        width: isMobile ? '100%' : '60vw',
                        maxWidth: 820,
                        maxHeight: isMobile ? '85vh' : '100%',
                        /*
                         * MUI's Drawer paper is content-box, so with a 100%
                         * height its padding is added on top and the foot of
                         * the drawer falls below the viewport — the bottom
                         * padding is then there but never reachable.
                         */
                        boxSizing: 'border-box',
                        p: 2,
                        // Clearance so the last hour row is not flush to the edge.
                        pb: { xs: 'calc(56px + env(safe-area-inset-bottom, 0px))', md: 8 },
                        bgcolor: BRAND.sand,
                    },
                }}
            >
                <Stack direction="row" justifyContent="flex-end">
                    <IconButton aria-label={t('close')} onClick={closeDrawer} size="small">
                        <CloseIcon />
                    </IconButton>
                </Stack>
                {drawerDate && (
                    <TimelineView
                        date={drawerDate}
                        events={drawerEvents}
                        openHours={openHours}
                        locale={locale}
                        t={t}
                        holidays={holidays}
                        closure={drawerClosure}
                        onOpenDetails={setSelectedEvent}
                    />
                )}
            </Drawer>
        </Box>
    );
}

function TaikenApp() {
    const payload = useMemo(readPayload, []);
    const theme = useMemo(createTaikenTheme, []);

    return (
        <ThemeProvider theme={theme}>
            <TaikenCalendar payload={payload} />
        </ThemeProvider>
    );
}

const mountEl = document.getElementById('taiken-hub-root');
if (mountEl) {
    createRoot(mountEl).render(<TaikenApp />);
}
